import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Store, Building2, Mail, Phone,
  CreditCard, Clock, CheckCircle, AlertCircle,
  Save, ShoppingBag, TrendingUp, Package, Copy,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:          { label: 'Pending',          color: 'bg-yellow-100 text-yellow-700' },
  confirmed:        { label: 'Confirmed',         color: 'bg-blue-100 text-blue-700' },
  preparing:        { label: 'Preparing',         color: 'bg-indigo-100 text-indigo-700' },
  ready:            { label: 'Ready',             color: 'bg-teal-100 text-teal-700' },
  out_for_delivery: { label: 'Out for Delivery',  color: 'bg-orange-100 text-orange-700' },
  delivered:        { label: 'Delivered',         color: 'bg-green-100 text-green-700' },
  cancelled:        { label: 'Cancelled',         color: 'bg-red-100 text-red-700' },
};

const VendorDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vendor, setVendor] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ordersData, setOrdersData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const [subscriptionPlan, setSubscriptionPlan] = useState('monthly');
  const [customEndDate, setCustomEndDate] = useState('');

  useEffect(() => {
    Promise.all([fetchVendor(), fetchSettings()]);
  }, [id]);

  const fetchVendor = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/vendors/${id}`, { withCredentials: true });
      setVendor(res.data);
      setCustomEndDate(res.data.subscription_end_date || '');
      fetchOrders();
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/settings`, { withCredentials: true });
      setSettings(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchOrders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/vendors/${id}/orders?limit=50`, { withCredentials: true });
      setOrdersData(res.data);
    } catch (e) { /* silent */ }
  };

  const calculateEndDate = (plan) => {
    const today = new Date();
    if (plan === 'monthly') {
      const d = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate());
      d.setDate(d.getDate() - 1);
      return d.toISOString().split('T')[0];
    } else if (plan === 'yearly') {
      const d = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      d.setDate(d.getDate() - 1);
      return d.toISOString().split('T')[0];
    }
    return '';
  };

  const handlePlanChange = (plan) => {
    setSubscriptionPlan(plan);
    setCustomEndDate(calculateEndDate(plan));
  };

  const handleUpdateSubscription = async () => {
    setSaving(true); setSaved(false);
    try {
      await axios.put(
        `${API_URL}/api/admin/vendors/${id}/subscription`,
        { subscription_plan: subscriptionPlan, subscription_end_date: customEndDate },
        { withCredentials: true }
      );
      setSaved(true);
      fetchVendor();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const copyId = () => {
    navigator.clipboard.writeText(id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isExpired = (d) => d && new Date(d) < new Date();
  const daysRemaining = (d) => {
    if (!d) return 0;
    return Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary" />
    </div>
  );

  if (!vendor) return (
    <div className="text-center py-12">
      <p className="text-text-muted mb-2">Vendor not found</p>
      <Button variant="link" onClick={() => navigate('/admin/vendors')}>Back to Vendors</Button>
    </div>
  );

  const expired = isExpired(vendor.subscription_end_date);
  const days = daysRemaining(vendor.subscription_end_date);
  const sc = ordersData?.status_counts || {};

  return (
    <div data-testid="vendor-details-page">
      {/* Back */}
      <Button
        variant="ghost"
        onClick={() => navigate('/admin/vendors')}
        className="mb-4 gap-2 text-text-secondary hover:text-text-primary"
      >
        <ArrowLeft size={18} /> Back to Vendors
      </Button>

      {/* ── Top Card ─────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-surface-border p-5 mb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">

          {/* Identity */}
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-primary/10 flex items-center justify-center shrink-0">
              <Store className="text-brand-primary" size={24} />
            </div>
            <div>
              <h1 className="font-heading text-xl font-bold text-text-primary leading-tight">
                {vendor.name}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                {vendor.email && (
                  <span className="flex items-center gap-1 text-text-secondary text-sm">
                    <Mail size={13} /> {vendor.email}
                  </span>
                )}
                {vendor.phone && (
                  <span className="flex items-center gap-1 text-text-secondary text-sm">
                    <Phone size={13} /> {vendor.phone}
                  </span>
                )}
                {vendor.apartment_name && (
                  <span className="flex items-center gap-1 text-text-muted text-sm">
                    <Building2 size={13} /> {vendor.apartment_name}
                  </span>
                )}
              </div>
              {/* Vendor ID */}
              <button
                onClick={copyId}
                data-testid="copy-vendor-id"
                className="mt-1.5 flex items-center gap-1.5 font-mono text-xs text-text-muted bg-surface-secondary px-2 py-0.5 rounded-md hover:bg-surface-border transition-colors w-fit"
              >
                <span>ID: {id}</span>
                <Copy size={11} className={copied ? 'text-green-500' : ''} />
                {copied && <span className="text-green-500 font-sans">Copied!</span>}
              </button>
              {/* Vendor Code */}
              {vendor.vendor_code && (
                <span className="mt-1 inline-block font-mono text-xs font-bold bg-brand-primary/10 text-brand-primary px-2 py-0.5 rounded-md tracking-widest">
                  {vendor.vendor_code}
                </span>
              )}
            </div>
          </div>

          {/* Subscription pills — inline */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Plan */}
            <div className="text-center">
              <p className="text-xs text-text-muted mb-1">Plan</p>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                vendor.subscription_plan === 'trial'   ? 'bg-yellow-100 text-yellow-700' :
                vendor.subscription_plan === 'monthly' ? 'bg-blue-100 text-blue-700' :
                vendor.subscription_plan === 'yearly'  ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {vendor.subscription_plan?.charAt(0).toUpperCase() + vendor.subscription_plan?.slice(1)}
              </span>
            </div>
            {/* Status */}
            <div className="text-center">
              <p className="text-xs text-text-muted mb-1">Status</p>
              {expired ? (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  <AlertCircle size={12} /> Expired
                </span>
              ) : (
                <span className="flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  <CheckCircle size={12} /> Active
                </span>
              )}
            </div>
            {/* Expires */}
            <div className="text-center">
              <p className="text-xs text-text-muted mb-1">{expired ? 'Expired on' : 'Expires'}</p>
              <div className={`flex items-center gap-1 text-sm font-semibold ${expired ? 'text-red-600' : days <= 7 ? 'text-amber-600' : 'text-text-primary'}`}>
                <Clock size={14} />
                {expired ? 'Expired' : `${days}d`}
              </div>
              <p className="text-xs text-text-muted">{vendor.subscription_end_date || '—'}</p>
            </div>
            {/* Payment */}
            <div className="text-center">
              <p className="text-xs text-text-muted mb-1">Payment</p>
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                vendor.payment_status === 'paid'    ? 'bg-green-100 text-green-700' :
                vendor.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                'bg-red-100 text-red-700'
              }`}>
                {vendor.payment_status?.charAt(0).toUpperCase() + vendor.payment_status?.slice(1)}
              </span>
            </div>
            {/* Start date */}
            <div className="text-center">
              <p className="text-xs text-text-muted mb-1">Started</p>
              <p className="text-sm font-medium text-text-primary">{vendor.subscription_start_date || '—'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Main 3-col grid ──────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Orders section — spans 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          {/* Quick stat strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Total Orders',  value: ordersData?.count ?? '—',          icon: ShoppingBag,  color: 'text-brand-primary',  bg: 'bg-brand-primary/10' },
              { label: 'Revenue',       value: ordersData ? `₹${(ordersData.total_revenue || 0).toLocaleString('en-IN')}` : '—', icon: TrendingUp, color: 'text-green-600', bg: 'bg-green-100' },
              { label: 'Delivered',     value: sc.delivered ?? '—',                icon: CheckCircle,  color: 'text-green-600',      bg: 'bg-green-100' },
              { label: 'Cancelled',     value: sc.cancelled ?? '—',                icon: AlertCircle,  color: 'text-red-500',        bg: 'bg-red-100' },
            ].map(({ label, value, icon: Icon, color, bg }) => (
              <div key={label} className="bg-white rounded-xl border border-surface-border p-4 flex items-center gap-3">
                <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
                  <Icon size={18} className={color} />
                </div>
                <div>
                  <p className="text-xs text-text-muted">{label}</p>
                  <p className="font-heading font-bold text-lg text-text-primary leading-tight">{value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Status breakdown */}
          {ordersData && (
            <div className="bg-white rounded-xl border border-surface-border p-4">
              <h3 className="font-heading font-semibold text-sm text-text-primary mb-3 flex items-center gap-2">
                <Package size={16} className="text-brand-primary" /> Order Breakdown
              </h3>
              <div className="flex flex-wrap gap-2">
                {Object.entries(sc).filter(([, v]) => v > 0).map(([status, count]) => {
                  const m = STATUS_META[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
                  return (
                    <span key={status} className={`px-3 py-1 rounded-full text-xs font-semibold ${m.color}`}>
                      {m.label}: {count}
                    </span>
                  );
                })}
                {Object.values(sc).every(v => v === 0) && (
                  <span className="text-text-muted text-sm">No orders yet</span>
                )}
              </div>
            </div>
          )}

          {/* Recent orders table */}
          <div className="bg-white rounded-xl border border-surface-border overflow-hidden">
            <div className="px-4 py-3 border-b border-surface-border">
              <h3 className="font-heading font-semibold text-sm text-text-primary">Recent Orders</h3>
            </div>
            {!ordersData || ordersData.orders.length === 0 ? (
              <div className="py-10 text-center text-text-muted text-sm">No orders yet</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-surface-border bg-background-secondary">
                      {['Order #', 'Customer', 'Amount', 'Items', 'Status', 'Date'].map(h => (
                        <th key={h} className="text-left px-4 py-2 text-xs font-semibold text-text-muted">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ordersData.orders.map((o) => {
                      const m = STATUS_META[o.status] || { label: o.status, color: 'bg-gray-100 text-gray-600' };
                      return (
                        <tr key={o.id} className="border-b border-surface-border last:border-0 hover:bg-background-secondary/50 transition-colors">
                          <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-primary">{o.order_number}</td>
                          <td className="px-4 py-3 text-text-primary">{o.customer_name || '—'}</td>
                          <td className="px-4 py-3 font-semibold">₹{(o.total_amount || 0).toLocaleString('en-IN')}</td>
                          <td className="px-4 py-3 text-text-secondary">{o.item_count}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${m.color}`}>{m.label}</span>
                          </td>
                          <td className="px-4 py-3 text-text-muted text-xs">
                            {o.created_at ? new Date(o.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Update Subscription — right sidebar */}
        <div className="bg-white rounded-2xl border border-surface-border p-5 h-fit">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-lg bg-brand-primary/10 flex items-center justify-center">
              <CreditCard className="text-brand-primary" size={17} />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-sm text-text-primary">Update Subscription</h2>
              <p className="font-body text-xs text-text-muted">Change plan or extend validity</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="font-body font-medium text-sm">Plan</Label>
              <Select value={subscriptionPlan} onValueChange={handlePlanChange}>
                <SelectTrigger className="rounded-xl text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">
                    Monthly {settings?.monthly_pricing ? `— ₹${settings.monthly_pricing}` : ''}
                  </SelectItem>
                  <SelectItem value="yearly">
                    Yearly {settings?.yearly_pricing ? `— ₹${settings.yearly_pricing}` : ''}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="end_date" className="font-body font-medium text-sm">Expiry Date</Label>
              <Input
                id="end_date"
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="rounded-xl text-sm"
              />
              <p className="text-xs text-text-muted">Adjust manually if needed</p>
            </div>

            <Button
              onClick={handleUpdateSubscription}
              disabled={saving || !customEndDate}
              className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2 text-sm"
            >
              {saving ? (
                <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />Saving...</>
              ) : (
                <><Save size={15} />Update Subscription</>
              )}
            </Button>

            {saved && (
              <p className="text-center text-green-600 text-xs font-medium">Subscription updated!</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorDetails;
